import { createContext, useContext, useEffect, type ReactElement, type ReactNode } from 'react';

import { initCookieConsent } from '../cookie-consent';
import { applyFairLanguage } from '../i18n';
import type { Fair, FairSibling } from '../types/api';

import { apiClient, type ApiError } from './client';
import { useApi } from './useApi';

interface FairContextValue {
  fair: Fair | undefined;
  siblings: FairSibling[];
  /**
   * The sibling representing this deployment. No endpoint returns it: the API
   * marks it with `isCurrent`, so it is derived here rather than in each of the
   * three components that render the country flag.
   */
  currentSibling: FairSibling | undefined;
  loading: boolean;
  error: ApiError | undefined;
}

const FairContext = createContext<FairContextValue | undefined>(undefined);

/**
 * The fair and its siblings are read by the header on every route and by several
 * pages. Fetching them once here keeps a route change from re-requesting what
 * does not change, and keeps the header from issuing its own duplicate call.
 */
export function FairProvider({ children }: { children: ReactNode }): ReactElement {
  const fair = useApi(() => apiClient.getFair());
  const siblings = useApi(() => apiClient.getFairSiblings());

  const languageId = fair.data?.language?.id;

  // Until the fair loads the UI stays in the default language; the loading
  // screen is the only thing shown in it. The cookie banner waits for the fair
  // too, so it opens in the fair's language rather than switching under the
  // visitor; it initializes once, and later calls are ignored.
  useEffect(() => {
    if (fair.data !== undefined) {
      initCookieConsent(applyFairLanguage(languageId));
    }
  }, [fair.data, languageId]);

  const list = siblings.data ?? [];

  return (
    <FairContext.Provider
      value={{
        fair: fair.data,
        siblings: list,
        currentSibling: list.find((sibling) => sibling.isCurrent) ?? list[0],
        loading: fair.loading,
        // Siblings are decorative — only a missing fair is a real failure.
        error: fair.error,
      }}
    >
      {children}
    </FairContext.Provider>
  );
}

export function useFair(): FairContextValue {
  const context = useContext(FairContext);

  if (context === undefined) {
    throw new Error('useFair must be used inside <FairProvider>');
  }

  return context;
}
